import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { isProjectInactive } from '../utils/projectStatus';
import { clearOrchestratorContext } from '../hooks/useOrchestratorContext';

interface Project {
  id: number;
  name: string;
  client_name: string;
  description: string;
  project_context: string;
  analysis_instructions: string;
  expire_date?: string;
  status: string;
  progress: number;
  documents_count: number;
  templates_count: number;
  created_at: string;
  updated_at: string;
  /** True for the original owner and for any co-owner. */
  is_owner?: boolean;
  is_collaborator?: boolean;
  collaborator_names?: string[];
  tenant_id?: number;
}

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  setCurrentProject: (project: Project | null) => void;
  createProject: (data: { name: string; client_name?: string; description?: string; project_context?: string; analysis_instructions?: string; expire_date?: string; client_tag_id?: number | null }) => Promise<boolean>;
  updateProject: (id: number, data: Partial<Project>) => Promise<boolean>;
  deleteProject: (id: number) => Promise<boolean>;
  refreshProjects: () => Promise<void>;
  loading: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProjectState] = useState<Project | null>(() => {
    const saved = localStorage.getItem('currentProject');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);

  const setCurrentProject = (project: Project | null) => {
    setCurrentProjectState(project);
    if (project) {
      localStorage.setItem('currentProject', JSON.stringify(project));
    } else {
      localStorage.removeItem('currentProject');
    }
  };

  const refreshProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/projects', {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }
      });

      if (response.data && response.data.projects) {
        const fetchedProjects = response.data.projects;
        setProjects(fetchedProjects);

        // Prefer an active project when (re)selecting a default so we never
        // auto-land the user on an expired/archived project. A saved project
        // that is still valid is always honoured (including if it is inactive
        // but the user explicitly chose it earlier — we keep it so a warning
        // banner can prompt them rather than silently switching).
        const firstActive = fetchedProjects.find((p: Project) => !isProjectInactive(p)) || fetchedProjects[0] || null;
        const pickDefault = () => {
          if (firstActive) {
            setCurrentProjectState(firstActive);
            localStorage.setItem('currentProject', JSON.stringify(firstActive));
          }
        };

        const savedProjectJSON = localStorage.getItem('currentProject');
        if (savedProjectJSON) {
          const savedProject = JSON.parse(savedProjectJSON);
          const matchingProject = fetchedProjects.find((p: Project) => p.id === savedProject.id);
          if (matchingProject) {
            setCurrentProjectState(matchingProject);
          } else {
            pickDefault();
          }
        } else {
          pickDefault();
        }
      } else {
        console.warn('No projects data in response:', response.data);
        setProjects([]);
      }
    } catch (error: any) {
      console.error('Projects API error:', error);
      if (error.response?.status >= 400) {
        toast.error('Failed to load projects');
      }
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  const createProject = async (data: { name: string; client_name?: string; description?: string; project_context?: string; analysis_instructions?: string; expire_date?: string; client_tag_id?: number | null }): Promise<boolean> => {
    try {
      const response = await api.post('/api/projects', data);
      await refreshProjects();
      // Creating a project switches "current project" every bit as much as picking one from
      // the dropdown does — ProjectSelector already clears the stored Smart Router/orchestrator
      // session before its own setCurrentProject() call (see clearOrchestratorContext there) so
      // a stale session tied to the PREVIOUS project can't silently reassert itself later. This
      // call was missing here, so useOrchestratorProjectGuard would still find the old session's
      // project id in sessionStorage on the next relevant mount, decide the "current" project
      // didn't match it, and silently call setCurrentProject() back to the OLD project — with
      // the header still showing the new project's name (a separate render already happened) but
      // every project-scoped read/write from that point on going to the wrong project. Confirmed
      // this caused real synthetic QA data to land in a real, differently-owned project.
      clearOrchestratorContext();
      // Set the new project as current
      if (response.data.project) {
        setCurrentProject(response.data.project);
      } else {
        setProjects(prevProjects => {
          setCurrentProject(prevProjects.length > 0 ? prevProjects[prevProjects.length - 1] : null);
          return prevProjects;
        });
      }
      toast.success('Project created successfully');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to create project';
      toast.error(message);
      return false;
    }
  };

  const updateProject = async (id: number, data: Partial<Project>): Promise<boolean> => {
    try {
      await api.put(`/api/projects/${id}`, data);
      await refreshProjects();
      toast.success('Project updated successfully');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update project';
      toast.error(message);
      return false;
    }
  };

  const deleteProject = async (id: number): Promise<boolean> => {
    try {
      await api.delete(`/api/projects/${id}`);
      await refreshProjects();
      if (currentProject?.id === id) {
        setCurrentProject(projects.length > 1 ? projects[0] : null);
      }
      toast.success('Project deleted successfully');
      return true;
    } catch (error: any) {
      const data = error.response?.data || {};
      const base = data.error || 'Failed to delete project';
      const detail = data.detail ? ` — ${data.detail}` : '';
      toast.error(`${base}${detail}`);
      return false;
    }
  };

  const value = {
    projects,
    currentProject,
    setCurrentProject,
    createProject,
    updateProject,
    deleteProject,
    refreshProjects,
    loading
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};